import { z } from "zod";
import { promptProblemSchema } from "./prompts.js";
/**
 * The prompt-research boundary.
 *
 * A model writes the questions. That is allowed — proposing is the only thing a
 * model may do here — and it is bounded by two things that are not negotiable
 * (AGENTS.md §14a):
 *
 *  1. **There is nowhere in this type to put an answer.** A candidate is a
 *     question and nothing else. No citation, no brand, no score. If a field
 *     for any of those ever appears, the model has been given a way to
 *     manufacture a measurement, and the tests fail on purpose.
 *  2. **Nothing here is saved.** Research returns candidates. A person reads
 *     them, and saving goes through the ordinary create-prompt route, which
 *     validates again. The model never writes to the library.
 */
/**
 * Where the questions are grown from.
 *
 * These three are a choice, not something the plan named, and the reasoning is
 * worth stating: each is a different thing a customer already has when they sit
 * down. `brand` is for a stranger with nothing but their own site. `topic` is
 * for someone who knows their category. `library` is for someone whose report
 * already works and wants more of what works.
 *
 * None of them reads a web page. A crawl brings robots handling, fetch-time
 * permission logging and a retention decision with it (§11), and that is a
 * feature of its own rather than a corner of this one.
 */
export const promptSeedSchema = z.discriminatedUnion("from", [
    /** What this company sells, in the customer's own words. */
    z.object({
        from: z.literal("brand"),
        brandId: z.string().min(1),
        /** One line on what they sell. Empty is allowed; it just produces less. */
        describes: z.string().trim().max(400).default(""),
    }),
    /** A category or job-to-be-done, typed by the customer. */
    z.object({
        from: z.literal("topic"),
        topic: z.string().trim().min(3).max(200),
    }),
    /**
     * More questions like the ones already saved. Uses the library as examples,
     * so it inherits the tone of a set that has already been approved.
     */
    z.object({
        from: z.literal("library"),
        /** Narrow the examples to one tag, when the library covers several areas. */
        tag: z.string().trim().min(1).max(40).optional(),
    }),
]);
export const researchRequestSchema = z.object({
    seed: promptSeedSchema,
    country: z
        .string()
        .trim()
        .length(2)
        .transform((value) => value.toUpperCase()),
    /** How many to ask for. The reply can be shorter — see `rejected`. */
    count: z.number().int().min(1).max(30).default(15),
});
/**
 * What a question is for, as the keyword writer judged it while writing it: a
 * buyer who has a problem, one weighing kinds of solution, one comparing
 * named options, or one ready to buy. A model's label on a proposal, so a
 * reader can pick by it — it says nothing about what an engine answers, and it
 * is never stored with the question (§14a).
 */
export const promptIntentSchema = z.enum([
    "problem_aware",
    "solution_aware",
    "comparison",
    "transactional",
]);
/**
 * A question offered to the customer. Nothing else, deliberately.
 *
 * There is no `answer`, no `citations`, no `expectedBrands` and no `score`, and
 * that absence is the load-bearing part of this file (§14a.3). `intent` is the
 * writer's label on the proposal: keyword research gives one, brand research
 * gives null, and a server from before 2026-10-13 leaves it out.
 */
export const promptCandidateSchema = z.object({
    text: z.string().min(1),
    intent: promptIntentSchema.nullable().optional(),
});
/**
 * A question the model produced and we threw away, with the reason.
 *
 * Reported rather than dropped silently. "We asked for 15 and got 11" is a fact
 * about the model that a customer should be able to see; hiding it would make a
 * bad seed look like a thin category (AGENTS.md §1).
 */
export const rejectedCandidateSchema = z.object({
    text: z.string(),
    problems: z.array(promptProblemSchema),
});
export const researchResultSchema = z.object({
    candidates: z.array(promptCandidateSchema),
    rejected: z.array(rejectedCandidateSchema),
    /**
     * Which model wrote them and what it cost. §14a.6 — a regression traces to a
     * version rather than to "the AI", and a budget nobody measures is not one.
     */
    model: z.string().min(1),
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
});
/**
 * Keywords in, questions out — one group of questions per keyword.
 *
 * A separate request from `researchRequestSchema` rather than a fourth seed on
 * it, because the *shape of the answer* is different. The three seeds above all
 * produce one flat list. This produces a list per keyword, and it has to: the
 * customer typed twenty searches and needs to see which questions came from
 * which one, or they cannot tell a keyword that produced nothing from a model
 * that was having a bad day.
 *
 * `language` is here and deliberately not on `researchRequestSchema`. Asking
 * the same question in two languages is two different measurements, not one —
 * and of everything we can vary, the language a buyer types in moves the answer
 * most. A screen that collects it and an API that ignores it would be a control
 * that does nothing, which is worse than no control.
 */
export const keywordResearchRequestSchema = z.object({
    /**
     * The searches the customer already has, one per entry.
     *
     * Twenty is the ceiling, and it is a cost ceiling: each keyword is its own
     * model call, so this number is a bill as well as a list length.
     */
    keywords: z.array(z.string().trim().min(2).max(120)).min(1).max(20),
    country: z
        .string()
        .trim()
        .length(2)
        .transform((value) => value.toUpperCase()),
    /** BCP-47 primary subtag — `en`, `hi`. The words the buyer types in. */
    language: z
        .string()
        .trim()
        .min(2)
        .max(8)
        .transform((value) => value.toLowerCase()),
    /** How many to ask for per keyword. The reply can be shorter. */
    perKeyword: z.number().int().min(1).max(15).default(10),
});
/**
 * One keyword and what it produced.
 *
 * Four fields say four different things, and collapsing any pair of them would
 * hide something a customer needs (AGENTS.md §1):
 *
 *  - `candidates` — questions that survived the check.
 *  - `rejected` — questions the model wrote that we refused, with the reason.
 *  - `duplicates` — questions this keyword produced that an earlier keyword had
 *    already produced. Not a fault; overlapping keywords overlap. But a group
 *    showing three questions out of ten asked for is explained by this number
 *    and looks like a thin keyword without it.
 *  - `failure` — a stable reason code when this keyword produced nothing at all.
 *    Null when it ran. A keyword whose model call failed and a keyword with no
 *    good questions are different facts and only one of them is our fault.
 */
export const keywordGroupSchema = z.object({
    /**
     * What produced this group.
     *
     * `label`, not `keyword`, because brand research fills the same shape with
     * "Category based", "Competitor based" and "Brand based". One group type
     * means one screen renders both, and a second near-identical component is
     * exactly the parallel version §15 forbids.
     */
    label: z.string().min(1),
    candidates: z.array(promptCandidateSchema),
    rejected: z.array(rejectedCandidateSchema),
    duplicates: z.number().int().min(0),
    failure: z.string().nullable(),
});
/**
 * A brand, and what its buyers ask. Three groups, never one flat list.
 *
 * The brand is typed rather than looked up by id: this runs before there is a
 * report, often before the brand exists as a row, which is the whole reason
 * this screen sits outside the brand section of the rail.
 */
export const brandResearchRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    /** A domain, not a URL. Reduced by the caller before it gets here. */
    domain: z.string().trim().min(3).max(253),
    /** Optional. It narrows the questions to the market they are actually in. */
    industry: z.string().trim().max(120).default(""),
    country: z
        .string()
        .trim()
        .length(2)
        .transform((value) => value.toUpperCase()),
    language: z
        .string()
        .trim()
        .min(2)
        .max(8)
        .transform((value) => value.toLowerCase()),
    /** How many to ask for per group. The reply can be shorter. */
    perGroup: z.number().int().min(1).max(20).default(15),
});
/**
 * The three ways a buyer arrives at a brand.
 *
 * `category` and `competitor` name no company at all — they are the questions
 * that decide whether you are *found*. `brand` names yours on purpose, and that
 * is a different measurement: the answer is certain to mention you, so it says
 * nothing about visibility. What it does say is how you are *described*, and
 * next to whom. The group is labelled so those two can never be added together.
 */
export const brandGroupKinds = ["category", "competitor", "brand"];
export const brandResearchResultSchema = z.object({
    groups: z.array(keywordGroupSchema),
    model: z.string().min(1),
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
});
export const keywordResearchResultSchema = z.object({
    /** In the order the keywords were sent, so a reader can find their own list. */
    groups: z.array(keywordGroupSchema),
    /** Which model wrote them and what the whole request cost (§14a.6). */
    model: z.string().min(1),
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
});
/**
 * Who a model thinks this brand competes with.
 *
 * Candidates only. Nothing is saved by asking, every website has already been
 * reduced to a registrable domain by the API, and a person edits the list
 * before a row exists. There is no field for a score or a rank — a guess about
 * who matters must never arrive looking like a measurement of who matters
 * (AGENTS.md §14a).
 */
export const suggestCompetitorsSchema = z.object({
    name: z.string().trim().min(1).max(120),
    website: z.string().trim().min(1).max(253),
    /**
     * What this company sells, when we were able to read its own pages.
     *
     * Optional because a site can refuse us, render in the browser, or be added
     * before a model was configured — and a competitor list from a name and a
     * domain is what this always did. But a name alone is a weak key: two
     * companies share one far more often than a buyer's shortlist tolerates, and
     * every comparison this product draws is built on the answer.
     */
    sells: z.string().trim().max(600).optional(),
    buyers: z.string().trim().max(400).optional(),
});
export const competitorSuggestionSchema = z.object({
    competitors: z.array(z.object({
        name: z.string(),
        /** Already reduced. "gitlab.com", never a full URL. */
        website: z.string(),
    })),
    /**
     * How many the model returned that we threw away — unkeyable, the brand
     * itself, or a repeat. Shown so a model whose websites stop resolving is
     * visible rather than just producing a shorter list.
     */
    discarded: z.number().int().min(0),
    model: z.string(),
});
//# sourceMappingURL=research.js.map